import graphql from "babel-plugin-relay/macro";
import { format } from "date-fns";
import { Box, Flex } from "grid-styled";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { createFragmentContainer } from "react-relay";
import system from "system-components";

import CopyLink from "pstat-design-system/inputs/CopyLink";
import { ClearableTextInput } from "pstat-design-system/inputs/TextInput";
import { H3 } from "pstat-design-system/text/headings";
import { Text } from "pstat-design-system/text/paragraph";
import { withLabelContext } from "pstat-anywhere/context_providers/LabelContext";

const Header = ({ documentLabel }) => {
  const { t } = useTranslation();
  return (
    <Flex justifyContent="space-between" mb={5}>
      <Box pr={5}>
        <H3 color="nav.0" fontWeight="normal">
          {t("documentControl.view.share.modal.readOnlyLinks.heading")}
        </H3>
        <Text>
          {t(
            "documentControl.view.share.modal.readOnlyLinks.headingDescription",
            {documentLabel}
          )}
        </Text>
      </Box>
      {/* <Box>
        <Button buttonStyle="secondary">
          {t("documentControl.view.share.modal.readOnlyLinks.create")}
        </Button>
      </Box> */}
    </Flex>
  );
};

const GridCell = system({
  pr: 5,
  mb: 4
}).extend`
  -ms-grid-column: ${props => props.column};
  -ms-grid-row: ${props => props.row};
`;

const GridHeaderCell = system({
  is: GridCell,
  pb: 2,
  mb: 2,
  borderBottom: 1,
  borderColor: "nav.80"
});

const LinkRowHeaderText = system({
  is: Text,
  display: "inline",
  color: "nav.25",
  fontSize: 1,
  fontWeight: "medium"
});

const DesktopLinkRow = props => (
  <Fragment>
    <GridCell row={props.row} column={1}>
      <ClearableTextInput
        value={props.description}
        onChange={props.onDescriptionChange}
      />
    </GridCell>
    <GridCell row={props.row} column={2}>
      <CopyLink fieldsetWidth="100%" url={props.url} />
    </GridCell>
    <GridCell row={props.row} column={3}>
      <LastReadDate date={props.dateLastAccessed} />
    </GridCell>
    {/* <Box>
      <Button buttonStyle="tertiary" py={0} px={2}>
        <TrashIcon />
      </Button>
    </Box> */}
  </Fragment>
);

const MobileRowSeparator = system({
  borderBottom: 1,
  borderColor: "nav.80",
  mb: 5,
  mt: 5,
  width: "100%"
});

const MobileLinkRow = props => {
  const { t } = useTranslation();
  return (
    <Fragment>
      <LinkRowHeaderText mb={1} display="inline-block">
        {t("documentControl.view.share.modal.readOnlyLinks.descriptionHeader")}
      </LinkRowHeaderText>
      <Flex>
        <Box flex={1}>
          <ClearableTextInput
            value={props.description}
            onChange={props.onDescriptionChange}
          />
        </Box>
      </Flex>
      <LinkRowHeaderText mb={1} mt={4} display="inline-block">
        {t("documentControl.view.share.modal.readOnlyLinks.linkHeader")}
      </LinkRowHeaderText>
      <CopyLink fieldsetWidth="100%" url={props.url} />
    </Fragment>
  );
};

const LinkRowHeaders = props => {
  const { t } = useTranslation();
  return (
    <Fragment>
      <GridHeaderCell row={1} column={1}>
        <LinkRowHeaderText>
          {t(
            "documentControl.view.share.modal.readOnlyLinks.descriptionHeader"
          )}
        </LinkRowHeaderText>
      </GridHeaderCell>
      <GridHeaderCell row={1} column={2}>
        <LinkRowHeaderText>
          {t("documentControl.view.share.modal.readOnlyLinks.linkHeader")}
        </LinkRowHeaderText>
      </GridHeaderCell>
      <GridHeaderCell row={1} column={3}>
        <LinkRowHeaderText>
          {t("documentControl.view.share.modal.readOnlyLinks.lastReadHeader")}
        </LinkRowHeaderText>
      </GridHeaderCell>
      {/* <Box>
        <LinkRowHeaderText>
          {t("documentControl.view.share.modal.readOnlyLinks.trashHeader")}
        </LinkRowHeaderText>
      </Box> */}
    </Fragment>
  );
};

const LastReadDate = props => (
  <Text color="nav.25">{format(props.date, "MMM D[,] YYYY")}</Text>
);

const TokenAccessContainer = system({
  p: 5,
  pb: 0,
  bg: "nav.95"
});

const GridContainer = system({
  display: "grid"
}).extend`
  display: -ms-grid;
  grid-template-columns: 1fr 1fr auto;
  -ms-grid-columns: 1fr 1fr auto;
  align-items: center;
`;

const TokenAccessContent = props => {
  if (!props.tokens) {
    return null;
  }
  const { documentLabel } = props.labels;
  const numberOfLinks = props.tokens.length;
  return (
    <Fragment>
      {props.tokens && (
        <TokenAccessContainer>
          <Header documentLabel={documentLabel} />
          {numberOfLinks > 0 && (
            <GridContainer>
              <LinkRowHeaders />
              {props.tokens.map((token, index) => (
                <DesktopLinkRow
                  row={index + 2} // first row of links is second row in grid
                  description={token.description}
                  url={token.url}
                  dateLastAccessed={token.dateLastAccessed}
                />
              ))}
            </GridContainer>
          )}
        </TokenAccessContainer>
      )}
    </Fragment>
  );
};

const MobileTokenAccessContent = props => {
  if (!props.tokens) {
    return null;
  }
  const { documentLabel } = props.labels;
  const numberOfLinks = props.tokens.length;
  return (
    <Fragment>
      {props.tokens && (
        <TokenAccessContainer pb={5}>
          <Header documentLabel={documentLabel} />
          {props.tokens.map((token, index) => (
            <Fragment>
              <MobileLinkRow description={token.description} url={token.url} />
              {index !== numberOfLinks - 1 && <MobileRowSeparator />}
            </Fragment>
          ))}
        </TokenAccessContainer>
      )}
    </Fragment>
  );
};

const TokenAccess = ({ isDesktop, ...otherProps }) => (
  <Fragment>
    {isDesktop ? (
      <TokenAccessContent {...otherProps} />
    ) : (
      <MobileTokenAccessContent {...otherProps} />
    )}
  </Fragment>
);

export default withLabelContext(createFragmentContainer(TokenAccess, {
  tokens: graphql`
    fragment TokenAccess_tokens on DocumentAccessTokenType
    @relay(plural: true) {
      description
      dateLastAccessed
      url
    }
  `
}));
